import * as angular from 'angular';
import * as _ from 'lodash';

import { ICustomPropertiesService } from '../../services';
import { VisibleObjectBase, GuidObjectType, ComponentDefinition, CustomReplacementDictionary, CustomFieldReplacement } from '../../models';

class CustomDictionary {
    [key: string] : string
}

class CustomPropertiesEditor implements angular.IComponentController {
    type: GuidObjectType;
    model: VisibleObjectBase;
    initial: { [key: string]: string } = {};
    inherited: CustomReplacementDictionary = {};
    rows: { key: string, value: string, inherited: CustomFieldReplacement }[] = [];
    propertyOverrided: number;
    propertyCount: number;
    inheritedCount: number;
    isDisabled: boolean;
    isLoading: boolean;
    isPopoverOpen = false;
    property = 'customProperties';
    formController: angular.IFormController;
    objectType = GuidObjectType;
    validation: { isValid: boolean, hasDuplicate: boolean, hasEmpty: boolean, hasInvalidCharacter: boolean, keyCount: any };

    private _timers: any[] = [];

    static $inject = ['$scope', '$timeout', 'customPropertiesService'];

    constructor(
        private _$scope: angular.IScope,
        private _$timeout: angular.ITimeoutService,
        private _customPropertiesService: ICustomPropertiesService) {
    }

    $onInit(): void {
        this.inherited = {};
        this._$scope.$watch(() => this.isPopoverOpen, () => {
            if (this.model) {
                this.switchPopover();
            }
        });
    }

    $onChanges(changesObj: Object): void {
        const model = changesObj['model'];
        if (model && model.currentValue) {
            if (this.isPopoverOpen) {
                this.closePopover(model.previousValue);
            }

            this.init();
        }
    }

    $onDestroy() {
        if (this.isPopoverOpen) {
            this.closePopover(this.model);
        }
        this._timers.forEach(t => this._$timeout.cancel(t));
    }

    addRow() {
        this.rows.push({ key: '', value: '', inherited: undefined });
        const idx = this.rows.length - 1;
        this.updateValidation();
        this._timers.push(this._$timeout(() => angular.element(`#row${idx}`).focus()));
        this.formController[this.property].$setDirty();
    }

    deleteRow(row, $event) {
        this.rows = this.rows.filter(r => r !== row);
        this.updateValidation();
        $event.stopPropagation();
        this.formController[this.property].$setDirty();
    }

    hasRow(key): boolean {
        return this.rows.filter(r => r.key === key).length > 0;
    }

    switchPopover() {
        if (!this.isPopoverOpen) {
            this.closePopover(this.model);
        } else {
            this.openPopover();
        }
    }

    private init() {
        this.isLoading = true;

        this.rows = this.mapRows();
        this.updateValidation();
        if (this.model && this.model.customProperties) {
            this.initial = angular.copy(this.model.customProperties);

            this.propertyCount = this.model && this.model.customProperties
                ? Object.keys(this.model.customProperties).length
                : 0;
        }

        if (this.model && this.model.guid && !(this.model as any).isCreating) {
            this.getInherited(this.model.guid)
                .then(herited => {
                    this.inheritedCount = Object.keys(herited).length;
                    this.inherited = herited;
                    this.propertyOverrided = this.countOverrided();
                })
                .catch(reason => {
                    // that's ok, probably just new identifier
                    this.inheritedCount = 0;
                    this.inherited = {};
                    this.propertyOverrided = 0;
                })
                .finally(() => this.isLoading = false);
        } else {
            this.isLoading = false;
        }
    }

    private mapRows() {
        if (this.model && this.model.customProperties) {
            return Object.keys(this.model.customProperties)
                .map(key => {
                    return {
                        key: key,
                        value: this.model.customProperties[key],
                        inherited: this.inherited[key]
                    };
                });
        } else {
            return [];
        }
    }

    private openPopover() {
        if (this.model && this.model.customProperties) {
            const newRows = this.mapRows();

            const inheritedRows = Object
                .keys(this.inherited)
                .filter(key => !(key in this.model.customProperties))
                .map(key => {
                    return { key: key, value: '', inherited: this.inherited[key] };
                });

            newRows.push(...inheritedRows);
            this.rows = newRows.sort((a, b) => a.key.toLowerCase().localeCompare(b.key.toLowerCase()));
            this.formController.popoverEdit = true;
            this.updateValidation();
        }
    }

    private closePopover(model: VisibleObjectBase) {
        const newDic = {};
        this.rows
            .filter(r => r.value || (r.key && !r.inherited))
            .forEach(r => newDic[r.key] = r.value);

        this.checkDifferences(this.initial, newDic);
        model.customProperties = newDic;

        this.propertyCount = Object.keys(model.customProperties).length;
        this.propertyOverrided = this.countOverrided();
        this.formController.popoverEdit = false;
        // don't feedback this error message as asked by #20187 and reset for us
        //this.formController[this.property].$setValidity('validity', this.isAllValid());
        this.formController[this.property].$setValidity('invalidCharacter', !this.hasInvalidCharacter());
        this.formController[this.property].$setValidity('duplicate', !this.hasDuplicate());
    }

    private isAllValid(): boolean {
        return _.every(this.rows, r => r.key);
    }

    private hasDuplicate(): boolean {
        const grouped = _.groupBy(this.rows, r => r.key);
        return _.some(grouped, x => x.length > 1);
    }

    private hasInvalidCharacter(): boolean {
        return _.some(this.rows, r => r.key.indexOf('=') > 0);
    }

    private checkDifferences(prevValue, newValue): void {
        const keys = Object.keys(prevValue);
        const areSame = keys.length === Object.keys(newValue).length &&
            _.every(keys, key => key in newValue && prevValue[key] === newValue[key]);

        if (areSame) {
            this.formController[this.property].$setPristine();
            if (this.formController.$$controls.filter(c => c.$dirty).length === 0) {
                this.formController.$setPristine();
            }
        } else {
            this.formController[this.property].$setDirty();
        }
    }

    private countOverrided(): number {
        if (this.model && this.model.customProperties) {
            return Object.keys(this.inherited)
                .filter(k => k in this.model.customProperties)
                .length;
        } else {
            return 0;
        }
    }

    private getInherited(guid: string): angular.IPromise<CustomReplacementDictionary> {
        return this._customPropertiesService
            .getCustomProperties(guid, this.type)
            .then(data => {
                const inherited = {};
                Object
                    .keys(data)
                    .forEach(k => inherited[k] = data[k]);
                return inherited;
            });
    }

    onChange() {
        this.formController[this.property].$setDirty();
        this.updateValidation();
    }

    private updateValidation(): void {
        const count = _.countBy(this.rows, r => r.key);
        const hasDuplicate = _.some(count, v => v > 1);
        const hasEmpty = _.some(this.rows, r => !r.key);
        const hasInvalidCharacter = _.some(this.rows, r => r.key?.indexOf('=') > 0);

        this.validation = {
            isValid: !hasDuplicate && !hasEmpty && !hasInvalidCharacter,
            hasDuplicate: hasDuplicate,
            hasEmpty: hasEmpty,
            hasInvalidCharacter: hasInvalidCharacter,
            keyCount: count
        };
    }
}

export const wtCustomPropertiesEditor: ComponentDefinition = {
    componentId: 'wtCustomPropertiesEditor',
    controller: CustomPropertiesEditor,
    template: require('./customPropertiesEditor.component.html'),
    controllerAs: 'vm',
    bindings: {
        model: '<',
        type: '@',
        formController: '<?',
        isDisabled: '<'
    }
};